import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import {
    ArrowLeft,
    Package,
    Calendar,
    Upload01,
    Eye,
    Download01,
    Trash01,
    XCircle,
    ClockRewind,
    SearchRefraction,
    AlertTriangle,
    Settings01,
    Dataflow03,
    Mail01,
    File02,
    FileCheck02,
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import RouteMap from '@/components/application/map/route-map';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Table, TableCard } from '@/components/application/table/table';
import { Tabs } from '@/components/application/tabs/tabs';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { useContainerDocuments, useDeleteDocument } from '@/hooks/api/use-documents';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { apiGet } from '@/lib/api-client';
import type { VersionCountResponse } from '@/types/api/documents';
import type { DocumentResponse } from '@/types/api/documents';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { useShipmentById } from '@/hooks/api/use-containers-recap';
import { transformShipmentToContainer } from '@/utils/shipment-to-container';
import { useMe } from '@/hooks/api/use-auth';
import { useDeleteShipment, useArchiveShipment, useSyncContainerTracking } from '@/hooks/api/use-shipments';
import { UserRole } from '@/types/api/auth';
import { toast } from '@/lib/toast-service';
import { ContainerActivityTimeline } from '@/components/application/containers/container-activity-timeline';
import { ContainerPhaseTimeline } from '@/components/application/containers/container-phase-timeline';
import { LiveTrackingSection } from '@/components/application/containers/live-tracking-section';
import { useContainerLatestTracking } from '@/hooks/api/use-container-tracking';
import { ContainerStakeholders } from '@/components/application/containers/container-stakeholders';
import { ContainerActions } from '@/components/application/containers/container-actions';
import { ContainerNotesTab } from '@/components/application/containers/container-notes-tab';
import { ContainerIssuesTab, useActiveIssuesCount } from '@/components/application/containers/container-issues-tab';
import { ContainerValidationTab, useValidationFailedCount } from '@/components/application/containers/container-validation-tab';
import { useContainerValidation } from '@/hooks/api/use-container-validation';
import { useContainerActivitiesPaginated } from '@/hooks/api/use-activities';
import { useContainerNotes } from '@/hooks/api/use-container-notes';
import { useSetting } from '@/hooks/api/use-settings';
import { ExtractionStatusBadge } from '@/components/application/documents/extraction-status-badge';
import { BackLink } from '@/components/base/back-link/back-link';
import { navigationStateFrom } from '@/types/navigation';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';
import { ValidationStatusBadge } from '@/components/application/documents/validation-status-badge';
import { FieldVerifyButton, ForensicsQuickActionsMenu } from '@/components/application/ai-chat/container-data-review-actions';
import { useChatAI } from '@/components/application/ai-chat/chat-ai-context';

// Helper to format time ago (e.g., "2 hours ago", "3 days ago")
const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('it-IT');
};

export const ContainerDetailPage = () => {
    const { t } = useTranslation(['pages:containers', 'common', 'types']);
    const { translateContainerStatus, translateRiskLevel } = useI18n();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const backFallback = searchParams.get('view') ? `/shipper/containers?view=${searchParams.get('view')}` : '/shipper/containers';
    const [selectedDocument, _setSelectedDocument] = useState<DocumentResponse | null>(null);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [moreSubTab, setMoreSubTab] = useState<'timeline' | 'notes'>('timeline');
    const [isRouteExpanded, setIsRouteExpanded] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
    const [showDeleteContainerModal, setShowDeleteContainerModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [archiveConfirmText, setArchiveConfirmText] = useState('');
    const [isArchiving, setIsArchiving] = useState(false);

    // Fetch current user for role check
    const { data: currentUser } = useMe();
    const { setAskEveClientContext } = useChatAI();

    // Check if user is admin (can delete)
    const isAdmin = currentUser?.role === UserRole.SUPER_ADMIN || currentUser?.role === UserRole.COMPANY_ADMIN;

    // Delete shipment mutation
    const deleteShipmentMutation = useDeleteShipment();

    // Archive shipment mutation
    const archiveShipmentMutation = useArchiveShipment();

    // Sync container tracking mutation
    const syncTrackingMutation = useSyncContainerTracking();

    // Read platform setting for refresh button visibility
    const { data: refreshSetting } = useSetting('tracking', 'enable_tracking_refresh');
    const isRefreshEnabled = refreshSetting?.value === true;

    // Fetch container from API
    const { data: shipmentData, isLoading } = useShipmentById(id);

    // Fetch documents from API
    const { data: documents = [], isLoading: isLoadingDocuments } = useContainerDocuments(id);

    // Fetch document types for name resolution (document_type_id → display name)
    const { data: documentTypesData } = useDocumentTypes();
    const docTypeNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if (documentTypesData) {
            for (const dt of documentTypesData) {
                map.set(dt.document_type_id, dt.name);
            }
        }
        return map;
    }, [documentTypesData]);

    // Fetch notes for count badge
    const { data: notesData } = useContainerNotes(id);

    // Delete document mutation
    const deleteDocumentMutation = useDeleteDocument();

    // Transform API response to Container type
    const container = useMemo(() => {
        if (!shipmentData) return null;
        return transformShipmentToContainer(shipmentData);
    }, [shipmentData]);

    const clearAskEveContext = useCallback(() => {
        setAskEveClientContext(null);
    }, [setAskEveClientContext]);

    useEffect(() => {
        if (!container) {
            clearAskEveContext();
            return;
        }
        setAskEveClientContext({
            surface: 'shipper_container_detail',
            route: location.pathname,
            entity: {
                kind: 'container',
                id: container.id,
                container_number: container.containerNumber ?? null,
                bl_number: container.blNumber ?? null,
            },
            labels: {
                page: t('list.title', { ns: 'pages:containers', defaultValue: 'Containers' }),
            },
        });
        return () => {
            clearAskEveContext();
        };
    }, [container, location.pathname, setAskEveClientContext, clearAskEveContext, t]);

    // Fetch JSONCargo live tracking data
    const {
        data: trackingData,
        isLoading: isTrackingLoading,
        isError: isTrackingError,
    } = useContainerLatestTracking(id, container?.containerNumber);

    // Fetch activities for phase timeline (shared cache with Timeline tab)
    const { activities: containerActivities = [] } = useContainerActivitiesPaginated(id);

    // Fetch active issues count for badge
    const activeIssuesCount = useActiveIssuesCount(id);
    const validationFailedCount = useValidationFailedCount(id, activeTab === 'documents');
    const { data: validationData } = useContainerValidation(id, { enabled: activeTab === 'documents' });

    // Documents sub-tab state
    const [docsSubTab, setDocsSubTab] = useState<'list' | 'validation'>('list');

    // Handle tab parameter from URL (timeline/notes are grouped under "More" in the UI)
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'timeline' || tabParam === 'notes') {
            setActiveTab(tabParam);
            setMoreSubTab(tabParam);
        } else if (tabParam && ['info', 'documents', 'issues'].includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const navigateTab = (tabId: string) => {
        setActiveTab(tabId);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', tabId);
        navigate(`/shipper/containers/${id}?${newSearchParams.toString()}`, { replace: true });
    };

    const handleMainTabSelection = (key: string) => {
        if (key === 'more') {
            navigateTab(moreSubTab);
        } else {
            navigateTab(key);
        }
    };

    const handleMoreSubTabSelection = (key: string) => {
        if (key !== 'timeline' && key !== 'notes') return;
        setMoreSubTab(key);
        navigateTab(key);
    };

    const mainTabSelectedKey = activeTab === 'timeline' || activeTab === 'notes' ? 'more' : activeTab;

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft': return 'gray';
            case 'Booked': return 'brand';
            case 'In Transit': return 'brand';
            case 'Arrived': return 'warning';
            case 'Delivered': return 'success';
            case 'On Hold': return 'warning';
            case 'Cancelled': return 'error';
            default: return 'gray';
        }
    };

    const handleDeleteDocument = (documentId: string) => {
        setDocumentToDelete(documentId);
    };

    const handleConfirmDelete = async () => {
        if (documentToDelete) {
            try {
                await deleteDocumentMutation.mutateAsync(documentToDelete);
                toast.success(t('detail.documents.deleteSuccess', 'Document deleted successfully'));
            } catch (error) {
                toast.error(t('detail.documents.deleteError', 'Failed to delete document'));
            }
            setDocumentToDelete(null);
        }
    };

    const handleConfirmDeleteContainer = async () => {
        if (!container || !id || deleteConfirmText !== 'DELETE') return;

        setIsDeleting(true);
        try {
            await deleteShipmentMutation.mutateAsync(id);
            toast.success(t('detail.confirmDeleteContainer.success', { containerNumber: container.containerNumber }));
            const viewParam = searchParams.get('view');
            const returnPath = viewParam ? `/shipper/containers?view=${viewParam}` : '/shipper/containers';
            navigate(returnPath);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('detail.confirmDeleteContainer.error');
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
            setShowDeleteContainerModal(false);
            setDeleteConfirmText('');
        }
    };

    const handleConfirmArchiveContainer = async () => {
        if (!container || !id || archiveConfirmText !== 'ARCHIVE') return;

        setIsArchiving(true);
        try {
            await archiveShipmentMutation.mutateAsync(id);
            toast.success(t('detail.confirmArchiveContainer.success', { containerNumber: container.containerNumber }));
            const viewParam = searchParams.get('view');
            const returnPath = viewParam ? `/shipper/containers?view=${viewParam}` : '/shipper/containers';
            navigate(returnPath);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('detail.confirmArchiveContainer.error');
            toast.error(errorMessage);
        } finally {
            setIsArchiving(false);
            setShowArchiveModal(false);
            setArchiveConfirmText('');
        }
    };

    const handleUploadDocument = () => {
        navigate(`/shipper/containers/${id}/upload`, { state: navigationStateFrom(location) });
    };

    const handleViewHistory = async (documentId: string) => {
        const state = navigationStateFrom(location);
        try {
            const versionCount = await apiGet<VersionCountResponse>(`/documents/${documentId}/version-count`);
            if (versionCount.version_count <= 1) {
                toast.info(t('detail.documents.noHistory', 'No history for this document'));
                return;
            }
            navigate(`/shipper/containers/${id}/documents/${documentId}/history`, { state });
        } catch {
            // If API fails, navigate anyway (history page will show empty state)
            navigate(`/shipper/containers/${id}/documents/${documentId}/history`, { state });
        }
    };

    const handleRefreshTracking = async () => {
        if (!id) return;
        try {
            const response = await syncTrackingMutation.mutateAsync(id);
            if (response.success) {
                const eventsMsg = response.events_created
                    ? ` (${response.events_created} events)`
                    : '';
                toast.success(t('detail.tracking.refreshSuccess', 'Tracking data updated') + eventsMsg);
            } else if (response.skipped) {
                toast.info(response.error || t('detail.tracking.refreshSkipped', 'Tracking not available for this container'));
            } else {
                toast.error(response.error || t('detail.tracking.refreshError', 'Failed to refresh tracking data'));
            }
        } catch {
            toast.error(t('detail.tracking.refreshError', 'Failed to refresh tracking data'));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading')}</p>
            </div>
        );
    }

    if (!container) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('detail.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('detail.notFound.message')}</p>
                <BackLink fallbackTo={backFallback} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover">
                    <ArrowLeft className="size-5" />
                    {t('detail.backToList')}
                </BackLink>
            </div>
        );
    }

    // Calculate total notes count for badge
    const totalNotesCount = (notesData?.internal_count || 0) + (notesData?.customer_count || 0);

    const tabItems = [
        { id: 'info', label: t('detail.tabs.info') },
        { id: 'documents', label: t('detail.tabs.documents') },
        {
            id: 'issues',
            label: (
                <span className="flex items-center gap-2">
                    {t('detail.tabs.issues', 'Issues')}
                    {activeIssuesCount > 0 && (
                        <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200">
                            {activeIssuesCount}
                        </span>
                    )}
                </span>
            ),
        },
        {
            id: 'more',
            label: (
                <span className="flex items-center gap-2">
                    {t('detail.tabs.more', 'More')}
                    <span className="hidden sm:inline text-xs font-normal text-tertiary">
                        ({t('detail.tabs.moreHint', 'timeline & notes')})
                    </span>
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <BackLink fallbackTo={backFallback} aria-label={t('detail.backToList')} />
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-semibold text-primary">{container.containerNumber || container.bookingNumber || container.blNumber || '–'}</h1>
                            <Badge color={getStatusColor(container.status)} size="sm">
                                {translateContainerStatus(container.status)}
                            </Badge>
                        </div>
                        <p className="text-tertiary mt-1">
                            {container.route.from} → {container.route.to}
                        </p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <ShipperOnboardingHeaderAction />
                    <ContainerActions
                        containerId={container.id}
                        containerNumber={container.containerNumber || ''}
                        enableRefresh={isRefreshEnabled}
                        onRefreshTracking={handleRefreshTracking}
                        isRefreshing={syncTrackingMutation.isPending}
                    />
                    <Link
                        to={`/shipper/containers/${container.id}/edit`}
                        state={navigationStateFrom(location)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        {t('detail.actions.edit')}
                    </Link>
                    {isAdmin && (
                        <>
                            <Button
                                onClick={() => setShowArchiveModal(true)}
                                color="secondary"
                                size="md"
                                iconLeading={Package}
                            >
                                {t('detail.actions.archive')}
                            </Button>
                            <Button
                                onClick={() => setShowDeleteContainerModal(true)}
                                color="primary-destructive"
                                size="md"
                                iconLeading={Trash01}
                            >
                                {t('detail.actions.delete')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Tracking Updates Info */}
            <div className="flex items-center gap-4 text-xs text-tertiary">
                <div className="flex items-center gap-1.5">
                    <Dataflow03 className="size-3.5" />
                    <span>
                        {container.lastTrackedAt
                            ? `${t('detail.tracking.lastJsonCargoUpdate', 'JSONCargo update')}: ${formatTimeAgo(container.lastTrackedAt)}`
                            : container.lastTrackingAttemptAt && container.lastTrackingAttemptResult === 'failed'
                              ? <span className="text-warning-secondary">{`${formatTimeAgo(container.lastTrackingAttemptAt)} — ${container.lastTrackingAttemptDetail || 'tracking failed'}`}</span>
                              : t('detail.tracking.noJsonCargoUpdate', 'No JSONCargo updates')}
                    </span>
                </div>
                <span className="text-quaternary">|</span>
                <div className="flex items-center gap-1.5">
                    <Mail01 className="size-3.5" />
                    <span>
                        {container.lastEmailUpdateAt
                            ? `${t('detail.tracking.lastEmailUpdate', 'Email update')}: ${formatTimeAgo(container.lastEmailUpdateAt)}${container.lastEmailSender ? ` — ${container.lastEmailSender}` : ''}`
                            : container.sourceEmailMessageId
                              ? (
                                    <Link
                                        to={`/shipper/activity?tab=trade-comms&messageId=${container.sourceEmailMessageId}`}
                                        className="text-primary hover:underline focus:outline-none focus:underline"
                                    >
                                        {t('detail.tracking.createdFromEmail', 'Created from imported email')}
                                    </Link>
                                )
                              : t('detail.tracking.noEmailUpdate', 'No email updates')}
                    </span>
                </div>
            </div>

            {/* Tabs — primary: info, documents, issues; timeline & notes under "More" */}
            <Tabs selectedKey={mainTabSelectedKey} onSelectionChange={(key) => handleMainTabSelection(key as string)}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab 1: Info */}
                <Tabs.Panel id="info" className="space-y-6">
                    <ContainerPhaseTimeline container={container} activities={containerActivities} trackingData={trackingData} />
                    <LiveTrackingSection
                        trackingData={trackingData}
                        isLoading={isTrackingLoading}
                        isError={isTrackingError}
                        hasContainerNumber={!!container.containerNumber}
                        onRefreshTracking={() => syncTrackingMutation.mutate(id!)}
                    />
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Container Info - Status & Progress */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-primary">{t('detail.info.containerInfo')}</h2>
                                <div className="flex items-center gap-3">
                                    <ForensicsQuickActionsMenu containerNumber={container.containerNumber} />
                                    <span className="text-xs text-tertiary">
                                        {t('detail.info.lastUpdate')}: {formatTimeAgo(container.updatedAt)}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.containerNumber', 'Container Number')}</span>
                                    <span className="text-sm text-primary font-mono flex items-center">
                                        {container.containerNumber || '–'}
                                        <FieldVerifyButton
                                            fieldLabel="Container Number"
                                            fieldName="container_number"
                                            currentValue={container.containerNumber}
                                            containerNumber={container.containerNumber}
                                        />
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.status')}</span>
                                    <Badge color={getStatusColor(container.status)} size="sm">
                                        {translateContainerStatus(container.status)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.progress')}</span>
                                    <div className="flex items-center gap-2">
                                        <ProgressBar
                                            value={container.progress}
                                            min={0}
                                            max={100}
                                        />
                                        <span className="text-sm text-primary">{container.progress}%</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.riskLevel')}</span>
                                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                        {translateRiskLevel(container.riskLevel)}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.activeIssues', 'Active Issues')}</span>
                                    <Badge color={container.alertCount && container.alertCount > 0 ? 'error' : 'success'} size="sm">
                                        {container.alertCount || 0}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.eta')}</span>
                                    <span className="text-sm text-primary flex items-center">
                                        {container.eta?.toLocaleDateString('it-IT') ?? '—'}
                                        <FieldVerifyButton
                                            fieldLabel="ETA (Estimated Arrival)"
                                            fieldName="estimated_arrival_date"
                                            currentValue={container.eta?.toLocaleDateString('it-IT') ?? null}
                                            containerNumber={container.containerNumber}
                                        />
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.value')}</span>
                                    <span className="text-sm text-primary">
                                        {container.value ? `${container.value.currency} ${container.value.amount.toLocaleString()}` : '—'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.bookingNumber', { defaultValue: 'Booking Number' })}</span>
                                    <span className="text-sm text-primary font-mono flex items-center">
                                        {container.bookingNumber || '–'}
                                        <FieldVerifyButton
                                            fieldLabel="Booking Number"
                                            fieldName="booking_number"
                                            currentValue={container.bookingNumber}
                                            containerNumber={container.containerNumber}
                                        />
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-tertiary">{t('detail.info.fields.blNumber', { defaultValue: 'BL Number' })}</span>
                                    <span className="text-sm text-primary font-mono flex items-center">
                                        {container.blNumber || '–'}
                                        <FieldVerifyButton
                                            fieldLabel="BL Number"
                                            fieldName="bl_number"
                                            currentValue={container.blNumber}
                                            containerNumber={container.containerNumber}
                                        />
                                    </span>
                                </div>
                                {/* Tracking info */}
                                {container.lastTrackedAt && (
                                    <div className="flex items-center justify-between pt-2 border-t border-secondary">
                                        <span className="text-sm text-tertiary">{t('detail.info.fields.lastTracked')}</span>
                                        <span className="text-sm text-primary">
                                            {formatTimeAgo(container.lastTrackedAt)}
                                            {container.trackingProvider && (
                                                <span className="text-tertiary ml-1">({container.trackingProvider})</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Route Info */}
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-primary">{t('detail.info.route')}</h2>
                                <Button
                                    size="sm"
                                    color="secondary"
                                    onClick={() => setIsRouteExpanded(v => !v)}
                                >
                                    {isRouteExpanded ? t('detail.info.collapseMap') : t('detail.info.expandMap')}
                                </Button>
                            </div>
                            <div className={`grid grid-cols-1 gap-4 ${isRouteExpanded ? '' : 'lg:grid-cols-2'}`}>
                                {!isRouteExpanded && (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <Package className="size-5 text-brand-600" />
                                            <div>
                                                <div className="text-sm font-medium text-primary">{t('detail.info.from')}</div>
                                                <div className="text-sm text-tertiary">{container.route.from}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Package className="size-5 text-success-600" />
                                            <div>
                                                <div className="text-sm font-medium text-primary">{t('detail.info.to')}</div>
                                                <div className="text-sm text-tertiary">{container.route.to}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Package className="size-5 text-tertiary" />
                                            <div>
                                                <div className="text-sm font-medium text-primary">{t('detail.info.fields.shippingCompany')}</div>
                                                <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Calendar className="size-5 text-tertiary" />
                                            <div>
                                                <div className="text-sm font-medium text-primary">{t('detail.info.created')}</div>
                                                <div className="text-sm text-tertiary">
                                                    {container.createdAt.toLocaleDateString('it-IT')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className={`h-80 rounded-lg overflow-hidden ring-1 ring-secondary ${isRouteExpanded ? '' : ''}`}>
                                    {/* Rotta marittima con waypoints realistici */}
                                    <RouteMap
                                        className="h-full z-0"
                                        container={container}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Transport & Cargo Details - Full Width Card */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.transportDetails')}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Vessel & Voyage */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide">{t('detail.info.sections.vessel')}</h3>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.vesselName')}</div>
                                    <div className="text-sm text-primary font-medium flex items-center">
                                        {container.vesselName || '-'}
                                        <FieldVerifyButton
                                            fieldLabel="Vessel Name"
                                            fieldName="vessel_name"
                                            currentValue={container.vesselName}
                                            containerNumber={container.containerNumber}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.voyageNumber')}</div>
                                    <div className="text-sm text-primary flex items-center">
                                        {container.voyageNumber || '-'}
                                        <FieldVerifyButton
                                            fieldLabel="Voyage Number"
                                            fieldName="voyage_number"
                                            currentValue={container.voyageNumber}
                                            containerNumber={container.containerNumber}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.carrier')}</div>
                                    <div className="text-sm text-primary flex items-center">
                                        {container.shippingCompany || '-'}
                                        <FieldVerifyButton
                                            fieldLabel="Carrier"
                                            fieldName="carrier_name"
                                            currentValue={container.shippingCompany}
                                            containerNumber={container.containerNumber}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.receiver')}</div>
                                    <div className="text-sm text-primary font-medium">{container.consigneeName || '-'}</div>
                                </div>
                            </div>

                            {/* Container & Transport */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide">{t('detail.info.sections.container')}</h3>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.containerType')}</div>
                                    <div className="text-sm text-primary">
                                        {container.containerType ? container.containerType.replace('_', ' ').toUpperCase() : '-'}
                                        {container.containerSize && ` (${container.containerSize})`}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.cargoType')}</div>
                                    <div className="text-sm text-primary">{container.cargoType?.toUpperCase() || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.transportMode')}</div>
                                    <div className="text-sm text-primary">{container.transportMode ? container.transportMode.charAt(0).toUpperCase() + container.transportMode.slice(1) : '-'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-tertiary">{t('detail.info.fields.incoterms')}</div>
                                    <div className="text-sm text-primary font-medium">{container.incoterms || '-'}</div>
                                </div>
                            </div>

                            {/* Cargo Info */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide">{t('detail.info.sections.cargo')}</h3>
                                {container.cargoDescription && (
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.description')}</div>
                                        <div className="text-sm text-primary">{container.cargoDescription}</div>
                                    </div>
                                )}
                                {container.hsCodePrimary && (
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.hsCode')}</div>
                                        <div className="text-sm text-primary font-mono">{container.hsCodePrimary}</div>
                                    </div>
                                )}
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.weight')}</div>
                                        <div className="text-sm text-primary">{container.totalWeightKg ? `${container.totalWeightKg.toLocaleString()} kg` : '-'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.volume')}</div>
                                        <div className="text-sm text-primary">{container.totalVolumeCbm ? `${container.totalVolumeCbm} m³` : '-'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.packages')}</div>
                                        <div className="text-sm text-primary">{container.totalPackages || '-'}</div>
                                    </div>
                                </div>
                                {/* Special indicators */}
                                <div className="flex flex-wrap gap-2 pt-1">
                                    {container.isHazardous && (
                                        <Badge color="error" size="sm">
                                            {t('detail.info.badges.hazardous')} {container.hazardClass && `(${container.hazardClass})`}
                                        </Badge>
                                    )}
                                    {container.requiresTemperatureControl && (
                                        <Badge color="brand" size="sm">
                                            {t('detail.info.badges.reefer')} {container.temperatureSetpointCelsius && `${container.temperatureSetpointCelsius}°C`}
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            {/* References & Dates */}
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide">{t('detail.info.sections.references')}</h3>
                                {container.bookingNumber && (
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.bookingNumber')}</div>
                                        <div className="text-sm text-primary font-mono">{container.bookingNumber}</div>
                                    </div>
                                )}
                                {container.purchaseOrderNumber && (
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.purchaseOrder')}</div>
                                        <div className="text-sm text-primary font-mono">{container.purchaseOrderNumber}</div>
                                    </div>
                                )}
                                {container.serviceContractNumber && (
                                    <div>
                                        <div className="text-xs text-tertiary">{t('detail.info.fields.serviceContract')}</div>
                                        <div className="text-sm text-primary font-mono">{container.serviceContractNumber}</div>
                                    </div>
                                )}
                                {/* Key dates */}
                                <div className="pt-2 border-t border-secondary space-y-2">
                                    {container.estimatedDepartureDate && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-tertiary">{t('detail.info.fields.etd')}</span>
                                            <span className="text-primary">{container.estimatedDepartureDate.toLocaleDateString('it-IT')}</span>
                                        </div>
                                    )}
                                    {container.actualDepartureDate && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-tertiary">{t('detail.info.fields.atd')}</span>
                                            <span className="text-primary">{container.actualDepartureDate.toLocaleDateString('it-IT')}</span>
                                        </div>
                                    )}
                                    {container.actualArrivalDate && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-tertiary">{t('detail.info.fields.ata')}</span>
                                            <span className="text-primary">{container.actualArrivalDate.toLocaleDateString('it-IT')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notes & Instructions - Only show if there are any */}
                    {(container.specialInstructions || container.customerNotes) && (
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.notes')}</h2>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {container.specialInstructions && (
                                    <div>
                                        <h3 className="text-sm font-medium text-secondary mb-2">{t('detail.info.fields.specialInstructions')}</h3>
                                        <p className="text-sm text-primary whitespace-pre-wrap bg-secondary/30 rounded-lg p-3">
                                            {container.specialInstructions}
                                        </p>
                                    </div>
                                )}
                                {container.customerNotes && (
                                    <div>
                                        <h3 className="text-sm font-medium text-secondary mb-2">{t('detail.info.fields.customerNotes')}</h3>
                                        <p className="text-sm text-primary whitespace-pre-wrap bg-secondary/30 rounded-lg p-3">
                                            {container.customerNotes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Stakeholders Section - Companies Involved */}
                    <ContainerStakeholders containerId={id} containerStatus={container.status} />
                </Tabs.Panel>

                {/* Tab 2: Documents */}
                <Tabs.Panel id="documents" className="space-y-6">
                    {/* Header with title + upload button */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-primary">{t('detail.documents.title')}</h2>
                        <Button
                            size="md"
                            color="primary"
                            className="inline-flex items-center gap-2"
                            onClick={handleUploadDocument}
                            iconLeading={Upload01}
                        >
                            {t('detail.documents.uploadDocument')}
                        </Button>
                    </div>

                    {/* Sub-tabs: List | Validation */}
                    <div className="flex gap-1 border-b border-secondary">
                        <button
                            onClick={() => setDocsSubTab('list')}
                            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                docsSubTab === 'list'
                                    ? 'border-brand-600 text-brand-600'
                                    : 'border-transparent text-tertiary hover:text-secondary'
                            }`}
                        >
                            <File02 className="h-4 w-4" />
                            <span>{t('detail.documents.subTabs.list', 'List')}</span>
                        </button>
                        <button
                            onClick={() => setDocsSubTab('validation')}
                            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                docsSubTab === 'validation'
                                    ? 'border-brand-600 text-brand-600'
                                    : 'border-transparent text-tertiary hover:text-secondary'
                            }`}
                        >
                            <FileCheck02 className="h-4 w-4" />
                            <span>{t('detail.documents.subTabs.validation', 'Validation')}</span>
                            {validationFailedCount > 0 && (
                                <span className="text-xs bg-error-100 text-error-700 px-1.5 py-0.5 rounded-full font-medium">
                                    {validationFailedCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Document validation mini summary */}
                    {validationData && activeTab === 'documents' && (
                        <div className="flex items-center gap-2 text-xs text-tertiary px-1 py-1.5">
                            {validationData.summary.missing_required_docs > 0 && (
                                <>
                                    <span className="text-warning-600 font-medium">
                                        {validationData.summary.missing_required_docs} missing
                                        {validationData.summary.missing_required_doc_types?.length > 0 && (
                                            <span className="text-tertiary font-normal">
                                                {' '}({validationData.summary.missing_required_doc_types.map((t: string) => t.replace(/_/g, ' ')).join(', ')})
                                            </span>
                                        )}
                                    </span>
                                    {(validationData.summary.cross_checks_total > 0 || validationData.summary.single_checks_total > 0) && (
                                        <span className="text-tertiary">&middot;</span>
                                    )}
                                </>
                            )}
                            {(validationData.summary.cross_checks_total > 0 || validationData.summary.single_checks_total > 0) ? (
                                <span className={
                                    (validationData.summary.failed + (validationData.summary.single_checks_total - validationData.summary.single_checks_passed)) > 0
                                        ? 'text-error-600 font-medium'
                                        : 'text-success-600 font-medium'
                                }>
                                    {validationData.summary.cross_checks_passed + validationData.summary.single_checks_passed}/
                                    {validationData.summary.cross_checks_total + validationData.summary.single_checks_total} checks passed
                                </span>
                            ) : (
                                validationData.summary.missing_required_docs === 0 && (
                                    <span>No checks run</span>
                                )
                            )}
                        </div>
                    )}

                    {/* Sub-tab: List (existing document table) */}
                    {docsSubTab === 'list' && (
                        <>
                            {isLoadingDocuments ? (
                                <div className="space-y-3 animate-pulse">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-secondary ring-1 ring-tertiary">
                                            <div className="size-10 rounded-lg bg-tertiary" />
                                            <div className="flex-1 space-y-2">
                                                <div className="h-4 w-48 rounded bg-tertiary" />
                                                <div className="h-3 w-24 rounded bg-tertiary" />
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="h-6 w-16 rounded-full bg-tertiary" />
                                                <div className="h-6 w-16 rounded-full bg-tertiary" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : documents.length === 0 ? (
                                <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                                    <Package className="size-12 mx-auto mb-4 text-tertiary" />
                                    <h3 className="text-lg font-medium text-primary mb-2">{t('detail.documents.noDocuments', 'No documents')}</h3>
                                    <p className="text-tertiary mb-4">{t('detail.documents.noDocumentsMessage', 'Upload documents to get started')}</p>
                                    <Button
                                        size="md"
                                        color="primary"
                                        onClick={handleUploadDocument}
                                        iconLeading={Upload01}
                                    >
                                        {t('detail.documents.uploadDocument')}
                                    </Button>
                                </div>
                            ) : (
                                <TableCard.Root>
                                    <Table
                                        onRowAction={(key) => {
                                            navigate(`/shipper/containers/${id}/documents/${key}?mode=view`, { state: navigationStateFrom(location) });
                                        }}
                                    >
                                        <Table.Header>
                                            <Table.Head label={t('detail.documents.headers.name')} isRowHeader />
                                            <Table.Head label={t('detail.documents.headers.type')} />
                                            <Table.Head label={t('detail.documents.headers.extraction', { defaultValue: 'Extraction' })} />
                                            <Table.Head label={t('detail.documents.headers.validation', { defaultValue: 'Validation' })} />
                                            <Table.Head label={t('detail.documents.headers.uploaded')} />
                                            <Table.Head label={t('detail.documents.headers.actions')} />
                                        </Table.Header>
                                        <Table.Body>
                                            {documents.map((document) => {
                                                return (
                                                    <Table.Row
                                                        key={document.document_id}
                                                        id={document.document_id}
                                                        className="cursor-pointer hover:bg-secondary/50"
                                                    >
                                                        <Table.Cell>
                                                            <div className="font-medium text-primary">{document.file_name || 'Untitled'}</div>
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <Badge color="brand" size="sm">
                                                                {(document.document_type_id && docTypeNameMap.get(document.document_type_id)) || document.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                                            </Badge>
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <ExtractionStatusBadge status={document.extraction_status} size="sm" />
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <ValidationStatusBadge status={document.validation_status} size="sm" />
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <span className="text-sm text-tertiary">
                                                                {new Date(document.upload_timestamp).toLocaleDateString('it-IT')}
                                                            </span>
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <Dropdown.Root>
                                                                <Dropdown.DotsHorizontalButton />
                                                                <Dropdown.Popover className="w-min">
                                                                    <Dropdown.Menu>
                                                                        <Dropdown.Item
                                                                            id="view"
                                                                            icon={Eye}
                                                                            label={t('detail.documents.actions.view')}
                                                                            onAction={() => navigate(`/shipper/containers/${id}/documents/${document.document_id}?mode=view`, { state: navigationStateFrom(location) })}
                                                                        />
                                                                        <Dropdown.Item
                                                                            id="review"
                                                                            icon={SearchRefraction}
                                                                            label={t('detail.documents.actions.review')}
                                                                            onAction={() => navigate(`/shipper/containers/${id}/documents/${document.document_id}?mode=review`, { state: navigationStateFrom(location) })}
                                                                        />
                                                                        <Dropdown.Item
                                                                            id="manager"
                                                                            icon={Settings01}
                                                                            label={t('detail.documents.actions.manager', { defaultValue: 'Manage' })}
                                                                            onAction={() => navigate(`/shipper/containers/${id}/documents/${document.document_id}?mode=manager`, { state: navigationStateFrom(location) })}
                                                                        />
                                                                        <Dropdown.Item
                                                                            id="history"
                                                                            icon={ClockRewind}
                                                                            label={t('detail.documents.actions.history')}
                                                                            onAction={() => handleViewHistory(document.document_id)}
                                                                        />
                                                                        <Dropdown.Item
                                                                            id="download"
                                                                            icon={Download01}
                                                                            label={t('detail.documents.actions.download')}
                                                                            onAction={() => {
                                                                                // Open download URL in new tab
                                                                                window.open(`/api/v1/documents/${document.document_id}/download`, '_blank');
                                                                            }}
                                                                        />
                                                                        <Dropdown.Item
                                                                            id="delete"
                                                                            icon={Trash01}
                                                                            label={t('detail.documents.actions.delete')}
                                                                            onAction={() => handleDeleteDocument(document.document_id)}
                                                                        />
                                                                    </Dropdown.Menu>
                                                                </Dropdown.Popover>
                                                            </Dropdown.Root>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                );
                                            })}
                                        </Table.Body>
                                    </Table>
                                </TableCard.Root>
                            )}
                        </>
                    )}

                    {/* Sub-tab: Validation */}
                    {docsSubTab === 'validation' && (
                        <ContainerValidationTab
                            containerId={id || ''}
                            containerNumber={container.containerNumber || ''}
                        />
                    )}
                </Tabs.Panel>

                <Tabs.Panel id="issues" className="space-y-6">
                    <ContainerIssuesTab containerId={id || ''} containerNumber={container.containerNumber || ''} />
                </Tabs.Panel>

                {/* Timeline & Notes (grouped under "More") */}
                <Tabs.Panel id="more" className="space-y-4">
                    <Tabs
                        selectedKey={moreSubTab}
                        onSelectionChange={(key) => handleMoreSubTabSelection(key as string)}
                    >
                        <Tabs.List
                            type="button-border"
                            size="sm"
                            items={[
                                {
                                    id: 'timeline',
                                    label: t('detail.tabs.timeline', 'Timeline'),
                                },
                                {
                                    id: 'notes',
                                    label: (
                                        <span className="flex items-center gap-2">
                                            {t('detail.tabs.notes', 'Notes')}
                                            {totalNotesCount > 0 && (
                                                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200">
                                                    {totalNotesCount}
                                                </span>
                                            )}
                                        </span>
                                    ),
                                },
                            ]}
                        >
                            {(item) => (
                                <Tabs.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Tabs.Item>
                            )}
                        </Tabs.List>
                        <Tabs.Panel id="timeline" className="space-y-6 mt-4">
                            <ContainerActivityTimeline containerId={id} limit={50} />
                        </Tabs.Panel>
                        <Tabs.Panel id="notes" className="space-y-6 mt-4">
                            <ContainerNotesTab containerId={id || ''} containerNumber={container.containerNumber || ''} />
                        </Tabs.Panel>
                    </Tabs>
                </Tabs.Panel>

            </Tabs>

            {/* Confirm Delete Container Modal */}
            {showDeleteContainerModal && (
                <ModalOverlay isOpen={showDeleteContainerModal} onOpenChange={(open) => {
                    if (!open) {
                        setShowDeleteContainerModal(false);
                        setDeleteConfirmText('');
                    }
                }}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="shrink-0 rounded-full bg-error-100 p-3 ring-1 ring-error-200">
                                        <AlertTriangle className="size-6 text-error-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-xl font-semibold text-primary">{t('detail.confirmDeleteContainer.title')}</h2>
                                            <button
                                                onClick={() => {
                                                    setShowDeleteContainerModal(false);
                                                    setDeleteConfirmText('');
                                                }}
                                                className="text-tertiary hover:text-primary"
                                            >
                                                <XCircle className="size-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="rounded-lg bg-error-50 p-4 ring-1 ring-error-200">
                                        <p className="text-sm text-error-800 font-medium">
                                            {t('detail.confirmDeleteContainer.warning')}
                                        </p>
                                    </div>

                                    <p className="text-tertiary">
                                        {t('detail.confirmDeleteContainer.message', { containerNumber: container?.containerNumber })}
                                    </p>

                                    <div>
                                        <label className="block text-sm font-medium text-primary mb-2">
                                            {t('detail.confirmDeleteContainer.typeDeleteLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            value={deleteConfirmText}
                                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                                            placeholder="DELETE"
                                            className="w-full px-3 py-2 rounded-lg border border-primary bg-primary text-primary placeholder-tertiary focus:outline-none focus:ring-2 focus:ring-error-500 focus:border-error-500"
                                            disabled={isDeleting}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => {
                                            setShowDeleteContainerModal(false);
                                            setDeleteConfirmText('');
                                        }}
                                        color="secondary"
                                        size="md"
                                        disabled={isDeleting}
                                    >
                                        {t('detail.confirmDeleteContainer.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDeleteContainer}
                                        color="primary-destructive"
                                        size="md"
                                        disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                                        isLoading={isDeleting}
                                    >
                                        {t('detail.confirmDeleteContainer.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Confirm Archive Container Modal */}
            {showArchiveModal && (
                <ModalOverlay isOpen={showArchiveModal} onOpenChange={(open) => {
                    if (!open) {
                        setShowArchiveModal(false);
                        setArchiveConfirmText('');
                    }
                }}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="shrink-0 rounded-full bg-warning-100 p-3 ring-1 ring-warning-200">
                                        <Package className="size-6 text-warning-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-xl font-semibold text-primary">{t('detail.confirmArchiveContainer.title')}</h2>
                                            <button
                                                onClick={() => {
                                                    setShowArchiveModal(false);
                                                    setArchiveConfirmText('');
                                                }}
                                                className="text-tertiary hover:text-primary"
                                            >
                                                <XCircle className="size-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="rounded-lg bg-warning-50 p-4 ring-1 ring-warning-200">
                                        <p className="text-sm text-warning-800 font-medium">
                                            {t('detail.confirmArchiveContainer.warning')}
                                        </p>
                                    </div>

                                    <p className="text-tertiary">
                                        {t('detail.confirmArchiveContainer.message', { containerNumber: container?.containerNumber })}
                                    </p>

                                    <div>
                                        <label className="block text-sm font-medium text-primary mb-2">
                                            {t('detail.confirmArchiveContainer.typeArchiveLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            value={archiveConfirmText}
                                            onChange={(e) => setArchiveConfirmText(e.target.value)}
                                            placeholder="ARCHIVE"
                                            className="w-full px-3 py-2 rounded-lg border border-primary bg-primary text-primary placeholder-tertiary focus:outline-none focus:ring-2 focus:ring-warning-500 focus:border-warning-500"
                                            disabled={isArchiving}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => {
                                            setShowArchiveModal(false);
                                            setArchiveConfirmText('');
                                        }}
                                        color="secondary"
                                        size="md"
                                        disabled={isArchiving}
                                    >
                                        {t('detail.confirmArchiveContainer.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmArchiveContainer}
                                        color="primary"
                                        size="md"
                                        disabled={archiveConfirmText !== 'ARCHIVE' || isArchiving}
                                        isLoading={isArchiving}
                                    >
                                        {t('detail.confirmArchiveContainer.archive')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Confirm Delete Document Modal */}
            {documentToDelete && (
                <ModalOverlay isOpen={!!documentToDelete} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('detail.documents.confirmDelete.title')}</h2>
                                    <button
                                        onClick={() => setDocumentToDelete(null)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary mb-6">{t('detail.documents.confirmDelete.message')}</p>
                                <div className="flex justify-end gap-3">
                                    <Button onClick={() => setDocumentToDelete(null)} color="secondary" size="md">
                                        {t('detail.documents.confirmDelete.cancel')}
                                    </Button>
                                    <Button onClick={handleConfirmDelete} color="primary-destructive" size="md">
                                        {t('detail.documents.confirmDelete.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Document Modal */}
            {showDocumentModal && selectedDocument && (
                <ModalOverlay isOpen={showDocumentModal} onOpenChange={setShowDocumentModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary mr-8 break-words">
                                        {t('detail.documents.documentDetails', 'Document Details')} - {selectedDocument.file_name || 'Untitled'}
                                    </h2>
                                    <button
                                        onClick={() => setShowDocumentModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    {/* Document Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-sm font-medium text-primary">{t('detail.documents.headers.type')}</div>
                                            <div className="text-sm text-tertiary">
                                                {(selectedDocument.document_type_id && docTypeNameMap.get(selectedDocument.document_type_id)) || selectedDocument.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-primary">{t('detail.documents.headers.extraction', { defaultValue: 'Extraction' })}</div>
                                            <ExtractionStatusBadge status={selectedDocument.extraction_status} size="sm" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-primary">{t('detail.documents.headers.validation', { defaultValue: 'Validation' })}</div>
                                            <ValidationStatusBadge status={selectedDocument.validation_status} size="sm" />
                                        </div>
                                    </div>

                                    {/* Document Metadata */}
                                    <div className="space-y-2">
                                        <div className="flex justify-between py-2 border-b border-secondary">
                                            <span className="text-sm font-medium text-primary">{t('detail.documents.headers.uploaded')}</span>
                                            <span className="text-sm text-tertiary">{new Date(selectedDocument.upload_timestamp).toLocaleString('it-IT')}</span>
                                        </div>
                                        {selectedDocument.file_size_bytes && (
                                            <div className="flex justify-between py-2 border-b border-secondary">
                                                <span className="text-sm font-medium text-primary">{t('detail.documents.fileSize', 'File Size')}</span>
                                                <span className="text-sm text-tertiary">{(selectedDocument.file_size_bytes / 1024).toFixed(1)} KB</span>
                                            </div>
                                        )}
                                        {selectedDocument.extraction_confidence && (
                                            <div className="flex justify-between py-2 border-b border-secondary">
                                                <span className="text-sm font-medium text-primary">{t('detail.documents.confidence', 'Confidence')}</span>
                                                <span className="text-sm text-tertiary">{(selectedDocument.extraction_confidence * 100).toFixed(0)}%</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
